import { useState, type SVGProps } from 'react';
import type { ConnectorCap, ConnectorDash, ConnectorEnd, ConnectorItem, ConnectorKind, ConnectorProps, ConnectorRoute } from '../../doc/schema';
import { ColorPicker, SizePicker } from '../../ui/ColorPicker';
import { useBoard, usePaneStore } from '../context';
import { useToolSettings, type ConnectorStyle } from '../toolSettings';
import { editConnectorLabel, restyleConnectors, styleOf } from './connectors';

const LINE_WIDTHS = [1, 2, 3, 5, 8, 12];
const BLOCK_WIDTHS = [16, 24, 40, 64, 96, 144];

// 16x16 icons in the toolbar's manner (square caps, 2px strokes)
const base: SVGProps<SVGSVGElement> = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'square',
  strokeLinejoin: 'miter',
  'aria-hidden': true,
};

const KINDS: { kind: ConnectorKind; label: string; title: string; d: string }[] = [
  { kind: 'line', label: 'Line', title: 'A line with end caps', d: 'M2 8h11M10 5l3 3-3 3' },
  { kind: 'block', label: 'Block', title: 'A fat block arrow', d: 'M2 6h6V3l6 5-6 5v-3H2z' },
];

const ROUTES: { route: ConnectorRoute; title: string; d: string }[] = [
  { route: 'straight', title: 'Straight (bend points make corners)', d: 'M2 13L14 3' },
  { route: 'elbow', title: 'Elbow: right angles', d: 'M2 13h6V3h6' },
  { route: 'curved', title: 'Curved: drag a wavy path to draw a snake', d: 'M2 13c6 0 6-10 12-10' },
];

const DASHES: { dash: ConnectorDash; title: string; d: string }[] = [
  { dash: 'solid', title: 'Solid', d: 'M2 8h12' },
  { dash: 'dashed', title: 'Dashed', d: 'M1 8h3M7 8h2M12 8h3' },
  { dash: 'dotted', title: 'Dotted', d: 'M2 8h0M6 8h0M10 8h0M14 8h0' },
];

const CAP_TITLE: Record<ConnectorCap, string> = {
  none: 'No cap',
  arrow: 'Arrow',
  triangle: 'Triangle',
  circle: 'Dot',
  diamond: 'Diamond',
};

/** A short line ending in the cap, pointing right (mirrored for the start). */
function CapIcon({ cap, mirror }: { cap: ConnectorCap; mirror?: boolean }) {
  return (
    <svg {...base}>
      <g transform={mirror ? 'translate(16 0) scale(-1 1)' : undefined}>
        {cap === 'none' && <path d="M2 8h12" />}
        {cap === 'arrow' && <path d="M2 8h11M9 4l4 4-4 4" />}
        {cap === 'triangle' && (
          <>
            <path d="M2 8h8" />
            <path d="M9 4l5 4-5 4z" fill="currentColor" stroke="none" />
          </>
        )}
        {cap === 'circle' && (
          <>
            <path d="M2 8h7" />
            <circle cx={11.5} cy={8} r={2.5} fill="currentColor" stroke="none" />
          </>
        )}
        {cap === 'diamond' && (
          <>
            <path d="M2 8h5" />
            <path d="M7 8l3.5-3.5L14 8l-3.5 3.5z" fill="currentColor" stroke="none" />
          </>
        )}
      </g>
    </svg>
  );
}

type Heads = 'end' | 'both' | 'none';
const HEADS: { heads: Heads; title: string; d: string }[] = [
  { heads: 'end', title: 'One head', d: 'M2 6h6V3l6 5-6 5v-3H2z' },
  { heads: 'both', title: 'Heads at both ends', d: 'M1 8l4-5v3h6V3l4 5-4 5v-3H5v3z' },
  { heads: 'none', title: 'No heads (a band)', d: 'M2 6h12v4H2z' },
];

function IconButton({ active, title, onClick, children }: { active: boolean; title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={`btn btn-icon${active ? ' is-active' : ''}`} title={title} aria-label={title} aria-pressed={active} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * A connector's look: line or block arrow, route, colours, width, dashes and end caps (heads for
 * block arrows). Used by the connector tool's options and by the inspector.
 */
export function ConnectorStyleFields({
  style,
  onChange,
  labelClass,
}: {
  style: ConnectorStyle;
  onChange: (patch: Partial<ConnectorStyle>) => void;
  labelClass: string;
}) {
  const block = style.kind === 'block';
  const heads: Heads = style.startCap !== 'none' && style.endCap !== 'none' ? 'both' : style.startCap !== 'none' || style.endCap !== 'none' ? 'end' : 'none';
  const setHeads = (h: Heads) => {
    if (h === 'none') onChange({ startCap: 'none', endCap: 'none' });
    else if (h === 'both') onChange({ startCap: 'triangle', endCap: 'triangle' });
    // one head: keep it where it is when there is one already
    else onChange(style.startCap !== 'none' && style.endCap === 'none' ? { startCap: 'triangle', endCap: 'none' } : { startCap: 'none', endCap: 'triangle' });
  };
  return (
    <>
      <div className="btn-row">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            type="button"
            className={`btn${style.kind === k.kind ? ' is-active' : ''}`}
            title={k.title}
            aria-pressed={style.kind === k.kind}
            onClick={() => onChange({ kind: k.kind })}
          >
            <svg {...base}>
              <path d={k.d} />
            </svg>
            {k.label}
          </button>
        ))}
      </div>
      <div className="btn-row">
        {ROUTES.map((r) => (
          <IconButton key={r.route} active={style.route === r.route} title={r.title} onClick={() => onChange({ route: r.route })}>
            <svg {...base}>
              <path d={r.d} />
            </svg>
          </IconButton>
        ))}
      </div>
      {block ? (
        <>
          <div className={labelClass}>Fill</div>
          <ColorPicker value={style.blockFill} onChange={(blockFill) => onChange({ blockFill })} label="Fill" />
          <div className={labelClass}>Outline</div>
          <ColorPicker value={style.blockOutline} onChange={(blockOutline) => onChange({ blockOutline })} allowNone label="Outline" />
          <div className={labelClass}>Width</div>
          <SizePicker value={style.blockWidth} options={BLOCK_WIDTHS} onChange={(blockWidth) => onChange({ blockWidth })} />
          <div className={labelClass}>Heads</div>
          <div className="btn-row">
            {HEADS.map((h) => (
              <IconButton key={h.heads} active={heads === h.heads} title={h.title} onClick={() => setHeads(h.heads)}>
                <svg {...base}>
                  <path d={h.d} />
                </svg>
              </IconButton>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className={labelClass}>Colour</div>
          <ColorPicker value={style.lineColor} onChange={(lineColor) => onChange({ lineColor })} label="Line colour" />
          <SizePicker value={style.lineWidth} options={LINE_WIDTHS} onChange={(lineWidth) => onChange({ lineWidth })} />
          <div className="btn-row">
            {DASHES.map((d) => (
              <IconButton key={d.dash} active={style.dash === d.dash} title={d.title} onClick={() => onChange({ dash: d.dash })}>
                <svg {...base}>
                  <path d={d.d} strokeLinecap={d.dash === 'dotted' ? 'round' : 'square'} strokeWidth={d.dash === 'dotted' ? 2.5 : 2} />
                </svg>
              </IconButton>
            ))}
          </div>
          <div className={labelClass}>Start</div>
          <div className="btn-row">
            {(['none', 'arrow', 'triangle', 'circle', 'diamond'] as ConnectorCap[]).map((c) => (
              <IconButton key={c} active={style.startCap === c} title={`Start: ${CAP_TITLE[c]}`} onClick={() => onChange({ startCap: c })}>
                <CapIcon cap={c} mirror />
              </IconButton>
            ))}
          </div>
          <div className={labelClass}>End</div>
          <div className="btn-row">
            {(['none', 'arrow', 'triangle', 'circle', 'diamond'] as ConnectorCap[]).map((c) => (
              <IconButton key={c} active={style.endCap === c} title={`End: ${CAP_TITLE[c]}`} onClick={() => onChange({ endCap: c })}>
                <CapIcon cap={c} />
              </IconButton>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/** What an end is attached to, in a few words. */
function endText(board: ReturnType<typeof useBoard>, end: ConnectorEnd): string {
  const target = end.item ? board.index.get(end.item) : undefined;
  if (!target) return 'loose';
  return `${target.type}${end.anchor ? ' (side dot)' : ''}`;
}

const LABEL_SIZES = [10, 12, 14, 16, 20, 24, 28, 32, 40, 48, 64, 80, 96];

/**
 * A connector's label: add (opens it for typing on the board) or remove it, its size and colour.
 * Not part of the tool's style: every new arrow starts without a label.
 */
function LabelFields({ items }: { items: ConnectorItem[] }) {
  const board = useBoard();
  const pane = usePaneStore();
  const [sizeText, setSizeText] = useState<string | null>(null);
  const first = items[0]!;
  const labelled = items.filter((i) => i.props.label);
  const shown = labelled[0] ?? first;
  const set = (props: Partial<ConnectorProps>) => board.updateItems(items.map((i) => [i.id, { props }]));
  const commitSize = (raw: string) => {
    setSizeText(null);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 4 && n <= 400) set({ labelSize: n });
  };
  return (
    <>
      <div className="insp-label">Label</div>
      <div className="btn-row">
        {items.length === 1 && (
          <button
            type="button"
            className="btn"
            title="Type text on the arrow (or double-click it, or press Enter)"
            disabled={board.isLocked(first)}
            onClick={() => editConnectorLabel({ board, pane }, first, null)}
          >
            {first.props.label ? 'Edit label' : 'Add label'}
          </button>
        )}
        <button type="button" className="btn" title="Take the text off" disabled={!labelled.length} onClick={() => board.updateItems(labelled.map((i) => [i.id, { props: { label: '' } }]))}>
          Remove
        </button>
      </div>
      {labelled.length > 0 && (
        <>
          <div className="insp-label">Label size</div>
          <div className="text-row">
            <input
              className="size-input"
              type="number"
              list="vds-label-sizes"
              min={4}
              max={400}
              value={sizeText ?? shown.props.labelSize}
              onChange={(e) => setSizeText(e.target.value)}
              onBlur={(e) => commitSize(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                e.stopPropagation();
              }}
              title="Label size"
            />
            <datalist id="vds-label-sizes">
              {LABEL_SIZES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div className="insp-label">Label colour ({shown.props.labelColor ?? (shown.props.kind === 'block' ? 'auto' : 'line')})</div>
          <ColorPicker
            value={shown.props.labelColor ?? 'none'}
            onChange={(c) => set({ labelColor: c === 'none' ? null : c })}
            allowNone
            label="Label colour"
          />
        </>
      )}
    </>
  );
}

/** Inspector fields for connectors: one, or several (the first one's style shows; changes go to all). */
export function ConnectorFields({ items }: { items: ConnectorItem[] }) {
  const board = useBoard();
  const tool = useToolSettings((t) => t.connector);
  const first = items[0]!;
  const bent = items.some((i) => i.props.bend.length > 0 || (i.props.route === 'elbow' && i.props.mid !== 0.5));
  return (
    <>
      <ConnectorStyleFields style={styleOf(first.props, tool)} onChange={(patch) => restyleConnectors(board, items, patch)} labelClass="insp-label" />
      <div className="insp-label">Path</div>
      <div className="btn-row">
        <button
          type="button"
          className="btn"
          title="Turn it round: the start and end caps change places"
          onClick={() => board.updateItems(items.map((i) => [i.id, { props: { startCap: i.props.endCap, endCap: i.props.startCap } }]))}
        >
          Swap ends
        </button>
        <button
          type="button"
          className="btn"
          title="Take out every bend point (an elbow's middle run goes back halfway)"
          disabled={!bent}
          onClick={() => board.updateItems(items.map((i) => [i.id, { props: { bend: [], mid: 0.5 } }]))}
        >
          Remove bends
        </button>
      </div>
      <LabelFields items={items} />
      {items.length === 1 && (
        <div className="insp-muted">
          Start: {endText(board, first.props.start)} · End: {endText(board, first.props.end)}
        </div>
      )}
    </>
  );
}
